import React, { useCallback, useMemo, useState } from 'react';
import MultiSelect, { SelectOption } from 'corp/components/UI/Select';
import { useCompassCard } from '@scale/compass-ui/store';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { CorpProjectGroupNodeType } from '@scale/shared/types/corpProjectGroup';
import { LintRule, ScriptLinterCondition, ValidatorCategory } from '@scale/shared/types/linter';
import { useProjectGroupNode } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import useLinterCard from './useLinterCard';
import { LinterLoading } from './LinterLoading';
import type { LinterContext } from '@scale/genai-compass-shared/cards/linter';
import { FormField, TextField } from '@scale/scaleui-radix';
import _ from 'lodash';
import { ReviewLevel } from '@scale/shared-types';
import { useDebounce } from 'react-use-v13';
import LinterError from './LinterError';

const REVIEW_LEVELS = (Object.values(ReviewLevel) as number[])
  .filter(
    level =>
      _.isNumber(level) &&
      level !== ReviewLevel.CalibrationAudit &&
      level !== ReviewLevel.Quarantined,
  )
  .sort((a, b) => a - b);

const LinterCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card } = useCompassCard(index);
  const { setProjectId, allowEditProjectId, setLinterNames, setReviewLevel } = useLinterCard(index);
  const context = card.defaultContext as LinterContext;

  const [debouncedProjectId, setDebouncedProjectId] = useState(context.projectId);
  useDebounce(() => setDebouncedProjectId(context.projectId), 500, [context.projectId]);

  const {
    data: project,
    isLoading,
    isError,
    error,
  } = useProjectGroupNode({ projectId: debouncedProjectId });

  const linterOptions = useMemo(() => {
    if (!project || project.type !== CorpProjectGroupNodeType.Project) {
      return [];
    }
    const lintRules: LintRule<ScriptLinterCondition>[] = project.lintRules;
    return lintRules
      .filter(lintRule => lintRule.condition.validatorCategory !== ValidatorCategory.MultiReplica)
      .map(lintRule => ({ label: lintRule.name, value: lintRule.name }));
  }, [project]);

  const handleProjectIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setProjectId(event.target.value);
      setDebouncedProjectId(event.target.value);
    },
    [setProjectId],
  );

  const handleReviewLevelChange = useCallback(
    (option: SelectOption) => {
      setReviewLevel(!_.isEmpty(option) ? (option.value as ReviewLevel) : undefined);
    },
    [setReviewLevel],
  );

  return (
    <div className="mb-4">
      {allowEditProjectId && (
        <FormField.Root size="3" className="mb-3 flex-row gap-4">
          <FormField.Label htmlFor="project-id-input" className="w-20 text-right justify-end">
            Project ID
          </FormField.Label>
          <TextField.Root
            id="project-id-input"
            className="flex-grow"
            value={context.projectId}
            onChange={handleProjectIdChange}
          />
        </FormField.Root>
      )}

      {!_.isEmpty(context.projectId) && !isError ? (
        <LinterLoading loading={isLoading}>
          <div className="mb-4">
            <div className="mb-2 text-sm font-medium text-neutral-600 group-focus/ScaleUISwitch:text-primary-400">
              Selected Linters
            </div>

            <MultiSelect
              options={linterOptions}
              placeholder="Select linters to run"
              onChange={(selectedOptions: any) => {
                const values = !_.isNil(selectedOptions)
                  ? _.castArray(selectedOptions).map(option => option.value)
                  : [];
                setLinterNames(values);
              }}
              value={context.linterNames}
              multi={true}
            />
          </div>

          <div className="mb-4">
            <div className="mb-2 text-sm font-medium text-neutral-600 group-focus/ScaleUISwitch:text-primary-400">
              Only the latest task attempt per task will be checked unless a specific review level
              is specified
            </div>

            <MultiSelect
              className="w-full rounded-lg"
              label="Select a review level"
              multi={false}
              options={REVIEW_LEVELS.map(level => ({
                label: `L${level}`,
                value: level,
              }))}
              disabled={false}
              placeholder={'review level'}
              isSearchable={true}
              isClearable={true}
              value={context.reviewLevel}
              onChange={handleReviewLevelChange}
            />
          </div>
        </LinterLoading>
      ) : (
        isError && <LinterError error={error} />
      )}
    </div>
  );
};

export default LinterCardComponent;
